// @ts-nocheck
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import { DeleteConsumeLater, GetConsumed, GetNotConsumed, PatchConsumeLater } from "./ApiRoutes";
import MainLoader, { ErrorResponse, SecondaryLoader } from "@/utils/Loaders";
import { Cookie, Drumstick, EyeClosedIcon, EyeIcon, Ghost, GripVertical, Trash, EllipsisVertical } from "lucide-react";
import { Link } from "react-router-dom";
import { ItemDetails } from "./ListViewer";
import { mediaTypeIcon } from "@/utils/TextUtils";
import { UserContext } from "@/App";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { useDebouncedCallback } from "use-debounce";
import { useInView } from 'react-intersection-observer'; 
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Popover, PopoverTrigger } from "./ui/popover";
import { PopoverContent } from "@radix-ui/react-popover";
import { motion } from "motion/react"

interface ConsumeItemType {
    title:string;
    image_url:string;
    status:boolean;
    external_id:string;
    item_type:"movies"|"shows"|"songs"|"albums"|"artists";
    added_at:string;
    artist?:string;
    year:string;
    
}

interface UpdatePayload {
  id: number;
  status: boolean;
}

interface UpdateItemsContextType {
    updateItems: UpdatePayload[];
    setUpdateItems: Dispatch<SetStateAction<UpdatePayload[]>>;
}

interface DeleteItemsContextType {
    deleteItems: UpdatePayload[];
    setDeleteItems: Dispatch<SetStateAction<UpdatePayload[]>>;
}


export const UpdatedItemsContext = createContext<UpdateItemsContextType>({
    updateItems: [],
    setUpdateItems: () => {}
  });

export const DeletedItemsContext = createContext<DeleteItemsContextType>({
    deleteItems: [],
    setDeleteItems: () => {}
  });


export default function ConsumeLater () {
    const [consumeUser, setConsumeUser] = useState<string>("");
    const [activeTab, setActiveTab] = useState<"consumed"|"not consumed">("not consumed");

    const [filterType, setFilterType] = useState<"movies"|"shows"|"songs"|"artists"|"albums"|"all">("all");
    const [updateItems, setUpdateItems] = useState<UpdatePayload[]>([]);
    const [deleteItems, setDeleteItems] = useState<UpdatePayload[]>([]);
    const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
    const [isDelete, setIsDelete] = useState<boolean>(false);

    const queryClient = useQueryClient();
    const { ref, inView } = useInView()

    useEffect(()=> {
        const pathSegments = location.pathname.split('/');
        const consumeSegment = pathSegments.indexOf("consumelater");

        if (consumeSegment !== -1 && consumeSegment < pathSegments.length - 1) {
            setConsumeUser(pathSegments[consumeSegment + 1]);
        }

    document.title = consumeUser ? `@${consumeUser} Consume Later` : "Profile — MediaHarbor";
        
    },[location.pathname,consumeUser])
    
    const isInitialMount = useRef(true);

    const {
        data,
        isPending,
        isSuccess,
        error,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        status,
    } =  useInfiniteQuery({
    queryKey: ['consumed-infinite', consumeUser, activeTab],
    queryFn: ({ pageParam = 1 }) => {
        if (activeTab === 'consumed') {
            return GetConsumed(consumeUser, pageParam);
        } else {
            return GetNotConsumed(consumeUser, pageParam);
        }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {

        if (lastPage && lastPage.next) {
            try {
                const url = new URL(lastPage.next);
                const nextPage = url.searchParams.get("page");
                return nextPage ? parseInt(nextPage, 10) : undefined;
            } catch (e) {
                return undefined;
            }
        }
        
        return undefined;
    },
    enabled: !!consumeUser,
});

    useEffect(() => {
        if (inView && hasNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, fetchNextPage]);

    const { mutate: bulkUpdateMutate, isPending: isBulkUpdatePending } = useMutation({
        mutationFn: (updates: UpdatePayload[]) => PatchConsumeLater(updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['consumed-infinite', consumeUser] });
          
            toast.success("Consume Later updated");
        },
        onError: (error) => {
            toast.error("Failed to save changes: " + error.message);
        }
    });

    const { mutate: bulkDeleteMutate, isPending: isBulkDeletePending } = useMutation({
        mutationFn: (updates: UpdatePayload[]) => DeleteConsumeLater(updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['consumed-infinite', consumeUser] });
            toast.success("Consume Later updated");
            setIsDelete(false);
            setIsDeleteOpen(false);
            setDeleteItems([]);
        },
        onError: (error) => {
            toast.error("Failed to save changes: " + error.message);
            setIsDelete(false);
            setIsDeleteOpen(false);
        }
    });

    const debouncedUpdate = useDebouncedCallback(() => {
        
        if (updateItems.length > 0) {
            bulkUpdateMutate(updateItems);
        }

        if(!isDelete && deleteItems.length > 0){
            setIsDeleteOpen(true);
        }

        if(isDelete){
            bulkDeleteMutate(deleteItems);
        }


    }, 250);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        debouncedUpdate();
    }, [updateItems, isDelete, deleteItems, debouncedUpdate]);

    
    if(isPending){
        return(
            <main className="flex flex-col  h-full w-full  items-center text-white">
                <header className="sticky items-center w-full top-0 rounded-md max-w-300 z-30 px-10 gap-2 backdrop-blur-xs flex flex-col text-white mt-10 py-5 font-semibold text-center">
                    <div className="flex absolute inset-0 bg-gradient-to-t from-neutral-950/20 via-neutral-950 to-neutral-950 z-10" />    
                    <div className="z-30">
                        <Link className="hover:underline mb-0" to={`/profile/${consumeUser}`}>{consumeUser}</Link>
                        <h1 className="text-xl sm:text-2xl flex justify-center items-center gap-2">
                            Consume Later 
                        </h1>
                        <section className="flex flex-wrap justify-center items-center gap-2 mt-2">
                        <Drumstick />
                        
                        </section>
                    </div>
                    <div className="flex items-center justify-center z-30 sm:w-3/4 px-4 h-10">
                        <Button onClick={() => setActiveTab("not consumed")} className={`hover:cursor-pointer rounded-none bg-transparent h-full text-base w-1/2 
                            ${activeTab == "not consumed" ? "border-b-4 border-white" : "border-b-2 border-neutral-500"}`}>
                                Not consumed
                        </Button>
                        <Button onClick={() => setActiveTab("consumed") } className={`hover:cursor-pointer rounded-none bg-transparent h-full text-base w-1/2 
                            ${activeTab == "consumed" ? "border-b-4 border-white" : "border-b-2 border-neutral-500"}`}>
                                Consumed
                        </Button>
                    </div>
                    </header>
                <SecondaryLoader />
            </main>
        )
        
    }
    if(isError){
        return(
          ErrorResponse(`${consumeUser}'s consume later`)
        )
    }
    if(isSuccess && data) {
        const allItems = data.pages.flatMap(page => page.results) ?? [];
        return(
            <UpdatedItemsContext value={{updateItems, setUpdateItems}} > 
            <DeletedItemsContext value={{deleteItems, setDeleteItems}} > 
            <main className="flex flex-col  h-full w-full  items-center text-white">
                <DeleteItemDialogue isOpen={Boolean(isDeleteOpen)} setIsOpen={setIsDeleteOpen} isDelete={Boolean(isDelete)} setIsDelete={setIsDelete} />
                <header className="sticky items-center w-full top-0 rounded-md max-w-300 z-30 px-10 gap-2 backdrop-blur-xs flex flex-col text-white mt-10 py-5 font-semibold text-center">
                    <div className="flex absolute inset-0 bg-gradient-to-t from-neutral-950/20 via-neutral-950 to-neutral-950 z-10" />    
                    <div className="z-30">
                        <Link className="hover:underline mb-0" to={`/profile/${consumeUser}`}>{consumeUser}</Link>
                        <h1 className="text-xl sm:text-2xl flex justify-center items-center gap-2">
                            Consume Later 
                        </h1>
                        <section className="flex flex-wrap justify-center items-center gap-2 mt-2">
                        <Drumstick />
                        
                        </section>
                    </div>
                    <div className="flex items-center justify-center z-30 sm:w-3/4 px-4 h-10">
                        <Button onClick={() => setActiveTab("not consumed")} className={`hover:cursor-pointer rounded-none bg-transparent h-full text-base w-1/2 
                            ${activeTab == "not consumed" ? "border-b-4 border-white" : "border-b-2 border-neutral-500"}`}>
                                Not consumed
                        </Button>
                        <Button onClick={() => setActiveTab("consumed") } className={`hover:cursor-pointer rounded-none bg-transparent h-full text-base w-1/2 
                            ${activeTab == "consumed" ? "border-b-4 border-white" : "border-b-2 border-neutral-500"}`}>
                                Consumed
                        </Button>
                    </div>
                    <div className="flex items-center justify-center mt-5 z-30 gap-2">
                        <div className="flex flex-col items-center gap-2 ">
                            <Label className="text-neutral-400" htmlFor="media-type">Media type</Label>
                            <Select value={filterType} defaultValue={filterType} onValueChange={value => setFilterType(value)}>
                                <SelectTrigger id="media-type" className=" sm:w-40 ">
                                    <SelectValue placeholder="Media type" />
                                </SelectTrigger>
                                <SelectContent className="bg-black text-secondary">
                                    <SelectItem className="hover:cursor-pointer" value="all">All types</SelectItem>
                                    <SelectItem className="hover:cursor-pointer" value="albums">Albums</SelectItem>
                                    <SelectItem className="hover:cursor-pointer" value="artists">Artists</SelectItem>
                                    <SelectItem className="hover:cursor-pointer" value="songs">Songs</SelectItem>
                                    <SelectItem className="hover:cursor-pointer" value="shows">Shows</SelectItem>
                                    <SelectItem className="hover:cursor-pointer" value="movies">Movies</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                    </div>
                    
                </header>
                
                <section className="flex flex-col gap-4 mt-10 pb-4">
                    {allItems.length < 1 && <Ghost className="text-neutral-500 w-7 h-7" />}


                    {allItems.filter((item,index) => {
                        if(filterType == "all"){
                            return true;
                        }
                        else if(item.item.item_type == filterType){
                            return true;
                        }
                        return false;
                    }).sort((a, b) => new Date(b.consumed_at) - new Date(a.consumed_at)).map((item) => ( // throws error but its ts so eh
                        <>
                        {
                            !item ? <Ghost className="text-white" />
                            :
                            <ConsumeItem
                            key={item.id} 
                            id={item.id} 
                            status={item.status} 
                            item={item.item} 
                            consumeUser={consumeUser} 
                            isDeleteOpen={isDeleteOpen}
                            setIsDeleteOpen={setIsDeleteOpen}
                        />
                        }
                        
                        </>
                    ))}

                    <div ref={ref}>
                        {isFetchingNextPage ? <SecondaryLoader /> : null}
                    </div>

                 
                    {!hasNextPage && allItems.length > 0 && (
                        <p className=" flex justify-center text-center text-neutral-700 font-bold italic uppercase mt-2">END</p>
                    )}
                </section>
                
            </main>
            </DeletedItemsContext>
            </UpdatedItemsContext>
        )
       
    }
    
}





interface ConsumeItemProps {
    item: ConsumeItemType;
    status:boolean;
    consumeUser:string;
    id:number;

    isDeleteOpen:boolean;
    setIsDeleteOpen: (value:boolean) => void;
}

function ConsumeItem({ item, consumeUser, status, id }: ConsumeItemProps) {
    const { user } = useContext(UserContext);
    const { setUpdateItems } = useContext(UpdatedItemsContext);
    const { setDeleteItems } = useContext(DeletedItemsContext);

    const [isQueuedForUpdate, setIsQueuedForUpdate] = useState(false);
    const [isQueuedForDelete, setIsQueuedForDelete] = useState(false);

    const username = user.username || localStorage.getItem("username");

    const handleUpdateClick = () => {
        setIsQueuedForUpdate(true); 
        setUpdateItems(prevUpdates => {
            const existingUpdateIndex = prevUpdates.findIndex(update => update.id === id);
            if (existingUpdateIndex !== -1) {
                const newUpdates = [...prevUpdates];
                newUpdates.splice(existingUpdateIndex, 1);
                setIsQueuedForUpdate(false); 
                return newUpdates;
            }
            return [...prevUpdates, { id: id, status: !status }];
        });
    };


    const handleDeleteClick = () => {
        setIsQueuedForDelete(true); 
        setDeleteItems(prevDeleteItems => {
            const existingDeleteIndex = prevDeleteItems.findIndex(delItem => delItem.id === id);
            if (existingDeleteIndex !== -1) {
                const newDeletes = [...prevDeleteItems];
                newDeletes.splice(existingDeleteIndex, 1);
                setIsQueuedForDelete(false); 
                return newDeletes;
            }

            return [...prevDeleteItems, { id, status }];
        });
    };
    

    const itemStyle = status
        ? "line-through bg-neutral-200/20 from-neutral-700/60 to-neutral-600/30"
        : "bg-black/60 from-black/70 to-transparent";

    return (
        <section className="flex justify-center items-center gap-2">
            <ItemDetails id={item.external_id} itemType={item.item_type}>
                <article className="relative justify-center items-center flex hover:cursor-pointer w-70 sm:w-120 md:w-150 h-20 rounded-2xl bg-center ease-in-out duration-200"
                    style={{ backgroundImage: `url(${item.image_url})` }}>
                    <div className={`group absolute flex justify-center items-center h-full w-full rounded-xl inset-0 bg-gradient-to-t duration-175 ease-in-out hover:bg-black/40 z-0 hover:backdrop-blur-none backdrop-blur-xs ${itemStyle}`}>
                        <h3 className="group flex justify-center text-center font-semibold z-10 max-w-40 md:max-w-[80%]">{item.title}</h3>
                        <div className="flex justify-center items-center absolute left-2.5 gap-2">{mediaTypeIcon(item.item_type)}</div>
                    </div>
                </article>
            </ItemDetails>
            
            {username === consumeUser && (
                <div className="flex items-center gap-2 flex-col sm:flex-row">
                    
                    <Popover>
                        <PopoverTrigger>
                            <EllipsisVertical className="hover:cursor-pointer" />
                        </PopoverTrigger>
                        <PopoverContent className="z-50 backdrop-blur-md rounded-xl bg-black/20">
                            <motion.main
                            className="p-4 gap-2 flex flex-col"
                            initial={{ opacity: 0 }}  animate={{ 
                            opacity: 1,
                            transition: {delay:0, duration:0.15}
                            }}
                            >
                                <Button onClick={handleUpdateClick} variant={"outline"} className="dark hover:cursor-pointer">
                                    {status ? "Not Consumed" : "Consumed"}
                                </Button>
                                <Button onClick={handleDeleteClick} variant={"destructive"} className="bg-red-700 hover:bg-red-800 hover:cursor-pointer">
                                    Delete
                                </Button>
                            </motion.main>
                            
                        </PopoverContent>
                    </Popover>
                   

                </div>
            )}
        </section>
    );
}

interface DeleteDialogProps {
    isOpen:boolean;
    setIsOpen: (value:boolean) => void;
    
    isDelete:boolean;
    setIsDelete:(value:boolean) => void;
}

function DeleteItemDialogue ({isOpen, setIsOpen, isDelete, setIsDelete}:DeleteDialogProps) {
    return(
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="bg-foreground text-secondary ">
                <DialogHeader>
                <DialogTitle>Remove items?</DialogTitle>
                <DialogDescription>
                    This is action cannot be undone
                </DialogDescription>
                </DialogHeader>
                 <section className="flex justify-center gap-4">
                    <Button className=" w-1/2" onClick={() => setIsOpen(false)}>No, I'm scared</Button>
                
                    <Button
                        className="hover:cursor-pointer w-1/2 bg-red-900 hover:bg-red-800 text-white"
                        onClick={() => setIsDelete(true)}
                    >
                        Yes, I'm sure
                    </Button>
                </section>
            </DialogContent>
        </Dialog>
    )
}